import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import Alert from '~/Common-Objects/UI/Alert'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import CheckBox from '~/Common-Objects/UI/Forms/CheckBox/CheckBox'
import Drawer from '~/Common-Objects/UI/Drawer/Drawer.jsx'
import DrawerConfirm from '~/Common-Objects/UI/Drawer/DrawerConfirm'
import Icons from '~/Common-Objects/UI/Icons/index'
import Paging from '~/Common-Objects/Paging'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import axios from '~/data/http/axios'
import i18n from '~/i18n'
import * as siteActions from '~/data/actions/siteActions'
import Mailchimp2Icons, { Mailchimp2MapCells } from './icons'
import MailchimpDiscoverLoading from './loading'
import FileManager from '~/pages/cp/includes/fileManager/fileManager'
import { IMPORT_PROGRESS_PATH, MAILCHIMP_FLOW_PATH } from '~/pages/cp/import/paths'
import { leaveMailchimpFlow } from '~/pages/cp/import/mailchimpFlowOrigin'
import { MAILCHIMP_APP_ID, startMailchimpOAuthConnect, consumeMailchimpJustConnected } from '~/utils/mailchimpOAuth'
import DateTimeFunctions from '~/Classes/dateTimeFunctions'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import {
    PAGE_SIZE,
    paginateItems,
    selectionMapForAll,
    isFullySelected,
    eligibleCampaignIds,
    optionalFieldTags,
    previewThumbModifier
} from './previewUtils'
import {
    captureMailchimpImportInteracted,
    captureMailchimpImportConnectClicked,
    captureMailchimpImportPreviewViewed,
    captureMailchimpImportStarted
} from '~/utils/mailchimpImportAnalytics'

const campaignHtmlCache = new Map()

function loadCampaignHtml(campaignId) {
    if (!campaignId) return Promise.resolve('')
    const existing = campaignHtmlCache.get(campaignId)
    if (existing) return existing
    const pending = axios
        .get('/application/mailchimp/campaigns/' + campaignId + '/content')
        .then((res) => (res.data && res.data.html) || '')
        .catch(() => '')
    campaignHtmlCache.set(campaignId, pending)
    return pending
}

function ThumbSkeleton() {
    return (
        <React.Fragment>
            <div className='mailchimp2-thumb__hero' />
            <div className='mailchimp2-thumb__line' />
            <div className='mailchimp2-thumb__line mailchimp2-thumb__line--mid' />
            <div className='mailchimp2-thumb__line mailchimp2-thumb__line--short' />
            <div className='mailchimp2-thumb__cta' />
        </React.Fragment>
    )
}

function TemplateThumb({ template, onOpen }) {
    const modifier = previewThumbModifier(template)
    const [html, setHtml] = useState('')
    useEffect(() => {
        if (modifier !== 'live') return undefined
        let cancelled = false
        loadCampaignHtml(template.sourceCampaignId).then((next) => {
            if (!cancelled) setHtml(next || '')
        })
        return () => {
            cancelled = true
        }
    }, [modifier, template.sourceCampaignId])

    if (modifier === 'plain') {
        return (
            <div className='mailchimp2-thumb__preview' aria-hidden='true'>
                <ThumbSkeleton />
            </div>
        )
    }

    const showLive = modifier === 'live' && !!html
    const className =
        'mailchimp2-thumb__preview mailchimp2-thumb__preview--open' +
        (modifier === 'image' ? ' mailchimp2-thumb__preview--image' : '') +
        (showLive ? ' mailchimp2-thumb__preview--live' : '')

    return (
        <button
            type='button'
            className={className}
            onClick={(event) => {
                event.stopPropagation()
                onOpen(template)
            }}
            aria-label={i18n.t('mailchimp2:preview.viewTemplate', { name: template.name })}
        >
            {modifier === 'image' ? (
                <img src={template.thumbnail} alt='' />
            ) : showLive ? (
                <span className='mailchimp2-thumb__frame-clip'>
                    <iframe className='mailchimp2-thumb__frame' tabIndex={-1} sandbox='' srcDoc={html} title='' />
                </span>
            ) : (
                <ThumbSkeleton />
            )}
        </button>
    )
}

function SelectAllToggle({ allSelected, onToggle, disabled }) {
    return (
        <button type='button' className='mailchimp2-select-all' onClick={onToggle} disabled={disabled}>
            {allSelected ? i18n.t('mailchimp2:preview.deselectAll') : i18n.t('mailchimp2:preview.selectAll')}
        </button>
    )
}

export default function Mailchimp2Config({ onClose }) {
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()
    const userToken = useSelector((store) => store.user.token)
    const trackingDomain = useSelector((store) => store.siteMaster.trackingDomain)
    const importAgain = typeof location.search === 'string' && new URLSearchParams(location.search).get('again') === '1'
    // Sticky for analytics: ?again=1 is dropped by history.replace, and the Done button never sets it
    const importAgainRequested = useRef(importAgain)
    const [loading, setLoading] = useState(true)
    const [loadingVariant, setLoadingVariant] = useState('connect')
    const [connected, setConnected] = useState(false)
    const [needsConnect, setNeedsConnect] = useState(false)
    const [loadError, setLoadError] = useState(false)
    const [discover, setDiscover] = useState(null)
    const [dndConverted, setDndConverted] = useState(0)
    const [job, setJob] = useState(null)
    const [accApp, setAccApp] = useState(null)
    const [busy, setBusy] = useState(false)

    const applyPayload = (res) => {
        setConnected(true)
        setDiscover(res.data.discover)
        setDndConverted(Number(res.data.dndConverted) || 0)
        const nextJob = res.data.job
        // Landing/CTA "Import" skips the Done screen and opens preview again
        if (importAgain && nextJob && nextJob.status === 'complete') {
            setJob(null)
            history.replace(MAILCHIMP_FLOW_PATH)
        } else {
            setJob(nextJob)
        }
        setAccApp(res.data.AccountApplication)
        if (nextJob && nextJob.status === 'running') {
            history.replace(IMPORT_PROGRESS_PATH)
        }
    }

    const beginOAuthConnect = async () => {
        setBusy(true)
        setLoadError(false)
        captureMailchimpImportConnectClicked()
        try {
            const res = await axios.get('/application/' + MAILCHIMP_APP_ID)
            startMailchimpOAuthConnect(res.data.Application, userToken, trackingDomain)
        } catch (err) {
            setLoadError(true)
            setBusy(false)
        }
    }

    const load = async () => {
        try {
            const justConnected = consumeMailchimpJustConnected()
            if (justConnected || importAgain) {
                // Fresh connect / import-again — loading Mailchimp data for preview
                setLoadingVariant('discover')
            } else {
                try {
                    const statusRes = await axios.get('/application/mailchimp/migrate')
                    const job = statusRes.data && statusRes.data.job
                    if (job && job.status === 'complete') {
                        setLoadingVariant('lastImport')
                    } else if (statusRes.data && statusRes.data.connected) {
                        setLoadingVariant('discover')
                    } else {
                        setLoadingVariant('connect')
                    }
                } catch (statusErr) {
                    setLoadingVariant('connect')
                }
            }
            const res = await axios.get('/application/mailchimp/discover')
            applyPayload(res)
            if (res.data && res.data.discover) {
                captureMailchimpImportPreviewViewed(res.data.discover)
            }
            setNeedsConnect(false)
            setLoadError(false)
        } catch (err) {
            // axios interceptor rejects with the response object (status on err.status)
            const status = err.status || (err.response && err.response.status)
            setConnected(false)
            setDiscover(null)
            setDndConverted(0)
            setJob(null)
            setAccApp(null)
            if (status === 400) {
                setNeedsConnect(true)
                setLoadError(false)
            } else {
                setNeedsConnect(false)
                setLoadError(true)
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    const startImport = async ({ listIds, templateIds, campaignIds, fieldTags }) => {
        setBusy(true)
        try {
            await axios.post('/application/mailchimp/migrate', { listIds, templateIds, campaignIds, fieldTags })
            captureMailchimpImportStarted({
                listIds,
                templateIds,
                campaignIds,
                fieldTags,
                importAgain: importAgainRequested.current
            })
            history.replace(IMPORT_PROGRESS_PATH)
            // Keep busy so Preview does not flash before this route unmounts
        } catch (err) {
            setBusy(false)
        }
    }

    const disconnect = () => {
        if (!accApp) return
        const content = (
            <DrawerConfirm
                header={i18n.t('mailchimp2:disconnect.header')}
                subheader={i18n.t('mailchimp2:disconnect.body')}
                confirm={async () => {
                    await axios.delete('/application/account/' + accApp.id)
                    setConnected(false)
                    setDiscover(null)
                    setJob(null)
                    setAccApp(null)
                }}
            />
        )
        dispatch(siteActions.alterSiteDrawer(true, true, 'right', content, true))
    }

    const notNow = () => {
        if (onClose) onClose()
        else leaveMailchimpFlow(history)
    }

    if (loading || (busy && !connected)) {
        // Connect click always uses connect copy; never "last import"
        const variant = busy && !connected ? 'connect' : loadingVariant
        return <MailchimpDiscoverLoading variant={variant} />
    }

    if (!connected) {
        if (needsConnect) {
            return (
                <div className='mailchimp2-page'>
                    <Mailchimp2Icons />
                    <h1 className='mailchimp2-title'>{i18n.t('mailchimp2:connect.title')}</h1>
                    <p className='mailchimp2-lead'>{i18n.t('mailchimp2:connect.lead')}</p>
                    {loadError && <p className='mailchimp2-lead'>{i18n.t('mailchimp2:connect.error')}</p>}
                    <Button
                        text={i18n.t('mailchimp2:connect.button')}
                        className='button-primary'
                        onClick={beginOAuthConnect}
                        disabled={busy}
                    />
                </div>
            )
        }
        return (
            <div className='mailchimp2-page'>
                <p className='mailchimp2-lead'>{i18n.t('mailchimp2:connect.error')}</p>
                <Button
                    text={i18n.t('mailchimp2:connect.retry')}
                    className='button-primary'
                    onClick={() => {
                        setLoading(true)
                        setLoadError(false)
                        load()
                    }}
                />
            </div>
        )
    }

    if (job && job.status === 'complete') {
        return (
            <Done
                job={job}
                disconnect={disconnect}
                importAgain={() => {
                    captureMailchimpImportInteracted('import_again')
                    importAgainRequested.current = true
                    setJob(null)
                }}
            />
        )
    }

    if (busy) {
        return (
            <div className='mailchimp2-page'>
                <Mailchimp2Icons animateConnector />
                <h1 className='mailchimp2-title'>{i18n.t('mailchimp2:import.title')}</h1>
                <div className='mailchimp2-progress'>
                    <div className='mailchimp2-progress__meta'>
                        <div className='mailchimp2-progress__label'>{i18n.t('mailchimp2:import.starting')}</div>
                        <div className='mailchimp2-progress__percent'>0%</div>
                    </div>
                    <div className='mailchimp2-progress__track'>
                        <div className='mailchimp2-progress__fill' style={{ width: '0%' }} />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Preview discover={discover} dndConverted={dndConverted} onStart={startImport} onNotNow={notNow} busy={busy} />
    )
}

function Preview({ discover, dndConverted, onStart, onNotNow, busy }) {
    const lists = (discover && discover.lists) || []
    const templates = (discover && discover.templates && discover.templates.items) || []
    const campaigns = (discover && discover.campaigns && discover.campaigns.items) || []
    const [selected, setSelected] = useState({})
    const [selectedTemplates, setSelectedTemplates] = useState({})
    const [selectedCampaigns, setSelectedCampaigns] = useState({})
    const [selectedFields, setSelectedFields] = useState({})
    const [previewTemplate, setPreviewTemplate] = useState(null)
    const [previewHtml, setPreviewHtml] = useState('')
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewError, setPreviewError] = useState('')
    const [templatePage, setTemplatePage] = useState(0)
    const [campaignPage, setCampaignPage] = useState(0)

    useEffect(() => {
        if (!previewTemplate) return undefined
        const onKey = (event) => {
            if (event.key !== 'Escape') return
            event.stopImmediatePropagation()
            setPreviewTemplate(null)
        }
        window.addEventListener('keydown', onKey, true)
        return () => window.removeEventListener('keydown', onKey, true)
    }, [previewTemplate])

    useEffect(() => {
        if (!previewTemplate || !previewTemplate.sourceCampaignId) {
            setPreviewHtml('')
            setPreviewError('')
            setPreviewLoading(false)
            return undefined
        }
        let cancelled = false
        setPreviewLoading(true)
        setPreviewError('')
        setPreviewHtml('')
        loadCampaignHtml(previewTemplate.sourceCampaignId).then((html) => {
            if (cancelled) return
            if (!html) setPreviewError(i18n.t('mailchimp2:preview.previewUnavailable'))
            else setPreviewHtml(html)
            setPreviewLoading(false)
        })
        return () => {
            cancelled = true
        }
    }, [previewTemplate])

    if (!discover) {
        return <MailchimpDiscoverLoading variant='discover' />
    }

    const listOn = (id) => selected[id] !== false
    const templateStartsSelected = (template) =>
        !(template && template.sourceCampaignId && (template.sendCount || 0) < 2)
    const templateOn = (id) => {
        if (Object.prototype.hasOwnProperty.call(selectedTemplates, id)) return selectedTemplates[id] !== false
        const template = templates.find((item) => item.id === id)
        return templateStartsSelected(template)
    }
    const campaignOn = (id) => selectedCampaigns[id] !== false
    const campaignAudienceSelected = (campaign) => !campaign.list_id || listOn(campaign.list_id)
    const listIds = lists.map((list) => list.id)
    const templateIds = templates.map((template) => template.id)
    const campaignEligibleIds = eligibleCampaignIds(campaigns, listOn)
    const selectedIds = lists.filter((list) => listOn(list.id)).map((list) => list.id)
    const selectedTemplateIds = templates.filter((template) => templateOn(template.id)).map((template) => template.id)
    const selectedCampaignIds = campaigns
        .filter((campaign) => campaignAudienceSelected(campaign) && campaignOn(campaign.id))
        .map((campaign) => campaign.id)
    const listsAllSelected = isFullySelected(listIds, selected, (id, map) => map[id] !== false)
    const templatesAllSelected = templateIds.length > 0 && templateIds.every((id) => templateOn(id))
    const campaignsAllSelected = isFullySelected(campaignEligibleIds, selectedCampaigns, (id, map) => map[id] !== false)
    const toggleList = (id, checked) => {
        setSelected((prev) => Object.assign({}, prev, { [id]: checked }))
    }
    const toggleTemplate = (id, checked) => {
        setSelectedTemplates((prev) => Object.assign({}, prev, { [id]: checked }))
    }
    const toggleCampaign = (id, checked) => {
        setSelectedCampaigns((prev) => Object.assign({}, prev, { [id]: checked }))
    }
    const toggleAllLists = () => {
        setSelected(selectionMapForAll(listIds, !listsAllSelected))
    }
    const toggleAllTemplates = () => {
        setSelectedTemplates(selectionMapForAll(templateIds, !templatesAllSelected))
    }
    const toggleAllCampaigns = () => {
        setSelectedCampaigns((prev) =>
            Object.assign({}, prev, selectionMapForAll(campaignEligibleIds, !campaignsAllSelected))
        )
    }

    const templatePaging = paginateItems(templates, templatePage, PAGE_SIZE)
    const campaignPaging = paginateItems(campaigns, campaignPage, PAGE_SIZE)

    const obviousFields = (discover.fields && discover.fields.obvious) || []
    const createFields = (discover.fields && discover.fields.create) || []
    const optionalTags = optionalFieldTags(obviousFields.concat(createFields))
    const fieldOn = (tag, mandatory) => (mandatory ? true : selectedFields[tag] !== false)
    const fieldsAllSelected = isFullySelected(optionalTags, selectedFields, (tag, map) => map[tag] !== false)
    const toggleField = (tag, mandatory) => {
        if (mandatory) return
        setSelectedFields((prev) => Object.assign({}, prev, { [tag]: !fieldOn(tag, false) }))
    }
    const toggleAllFields = () => {
        setSelectedFields((prev) => Object.assign({}, prev, selectionMapForAll(optionalTags, !fieldsAllSelected)))
    }
    const selectedFieldTags = []
    for (const field of obviousFields) {
        if (fieldOn(field.tag, field.mandatory)) selectedFieldTags.push(field.tag)
    }
    for (const field of createFields) {
        if (fieldOn(field.tag, field.mandatory)) selectedFieldTags.push(field.tag)
    }
    const dndBlocked = selectedTemplateIds.length + selectedCampaignIds.length > 100 - (Number(dndConverted) || 0)

    return (
        <div className='mailchimp2-page'>
            <Mailchimp2Icons />
            <h1 className='mailchimp2-title'>{i18n.t('mailchimp2:preview.title')}</h1>
            <p className='mailchimp2-lead'>{i18n.t('mailchimp2:preview.lead')}</p>

            <Paper className='mailchimp2-card'>
                <div className='mailchimp2-card__head'>
                    <h3>{i18n.t('mailchimp2:preview.audiences')}</h3>
                    {lists.length > 0 && <SelectAllToggle allSelected={listsAllSelected} onToggle={toggleAllLists} />}
                </div>
                <p>{i18n.t('mailchimp2:preview.audiencesLead')}</p>
                <div className='mailchimp2-map-table'>
                    {lists.map((list) => (
                        <div
                            className={'mailchimp2-row mailchimp2-row--pick' + (listOn(list.id) ? '' : ' is-off')}
                            key={list.id}
                            onClick={(event) => {
                                if (event.target.closest && event.target.closest('.checkboxWrapper')) return
                                toggleList(list.id, !listOn(list.id))
                            }}
                        >
                            <div className='mailchimp2-map-table__cell mailchimp2-map-table__check'>
                                <CheckBox
                                    checked={listOn(list.id)}
                                    onClick={(event) => toggleList(list.id, event.target.checked)}
                                />
                            </div>
                            <Mailchimp2MapCells
                                from={list.name}
                                fromSub={i18n.t('mailchimp2:preview.members', { count: list.member_count })}
                                to={list.groupName || list.name}
                            />
                        </div>
                    ))}
                </div>
            </Paper>

            <Paper className='mailchimp2-card'>
                <div className='mailchimp2-card__head'>
                    <h3>{i18n.t('mailchimp2:preview.fields')}</h3>
                    {optionalTags.length > 0 && (
                        <SelectAllToggle allSelected={fieldsAllSelected} onToggle={toggleAllFields} />
                    )}
                </div>
                <p>{i18n.t('mailchimp2:preview.fieldsLead')}</p>
                <div className='mailchimp2-chips'>
                    {obviousFields.map((field) => {
                        const on = fieldOn(field.tag, field.mandatory)
                        const className =
                            'mailchimp2-chip' +
                            (field.mandatory ? ' mailchimp2-chip--mandatory' : ' mailchimp2-chip--button') +
                            (on ? '' : ' is-off')
                        const label = field.transpond || field.name
                        if (field.mandatory) {
                            return (
                                <span className={className} key={field.tag} aria-disabled='true'>
                                    {label}
                                </span>
                            )
                        }
                        return (
                            <button
                                type='button'
                                className={className}
                                key={field.tag}
                                aria-pressed={on}
                                onClick={() => toggleField(field.tag, false)}
                            >
                                {label}
                            </button>
                        )
                    })}
                    {createFields.map((field) => {
                        const on = fieldOn(field.tag, field.mandatory)
                        const className =
                            'mailchimp2-chip' +
                            (field.mandatory ? ' mailchimp2-chip--mandatory' : ' mailchimp2-chip--button') +
                            (on ? '' : ' is-off')
                        const label = field.name
                        if (field.mandatory) {
                            return (
                                <span className={className} key={field.tag} aria-disabled='true'>
                                    {label}
                                </span>
                            )
                        }
                        return (
                            <button
                                type='button'
                                className={className}
                                key={field.tag}
                                aria-pressed={on}
                                onClick={() => toggleField(field.tag, false)}
                            >
                                {label}
                            </button>
                        )
                    })}
                </div>
            </Paper>

            <Paper className='mailchimp2-card'>
                <div className='mailchimp2-card__head'>
                    <h3>{i18n.t('mailchimp2:preview.templates')}</h3>
                    {templates.length > 0 && (
                        <SelectAllToggle allSelected={templatesAllSelected} onToggle={toggleAllTemplates} />
                    )}
                </div>
                <p>{i18n.t('mailchimp2:preview.templatesLead', { count: selectedTemplateIds.length })}</p>
                <p className='mailchimp2-note'>
                    {i18n.t('mailchimp2:preview.templatesNote')}
                    {templates.length > 0 && (
                        <React.Fragment> {i18n.t('mailchimp2:preview.templatesSelected')}</React.Fragment>
                    )}
                </p>
                {templates.length > 0 && (
                    <React.Fragment>
                        <div className='mailchimp2-thumbs'>
                            {templatePaging.pageItems.map((template) => (
                                <div
                                    className={'mailchimp2-thumb' + (templateOn(template.id) ? '' : ' is-off')}
                                    key={template.id}
                                    onClick={(event) => {
                                        if (event.target.closest && event.target.closest('.checkboxWrapper')) return
                                        if (event.target.closest && event.target.closest('.mailchimp2-thumb__preview'))
                                            return
                                        toggleTemplate(template.id, !templateOn(template.id))
                                    }}
                                >
                                    <CheckBox
                                        checked={templateOn(template.id)}
                                        onClick={(event) => toggleTemplate(template.id, event.target.checked)}
                                    />
                                    <TemplateThumb template={template} onOpen={setPreviewTemplate} />
                                    <div className='mailchimp2-thumb__copy'>
                                        <div className='mailchimp2-thumb__name'>{template.name}</div>
                                        {template.sendCount > 0 && (
                                            <div className='mailchimp2-thumb__meta'>
                                                {template.sendCount === 1
                                                    ? i18n.t('mailchimp2:preview.templatesUsedOne')
                                                    : i18n.t('mailchimp2:preview.templatesUsed', {
                                                          count: template.sendCount
                                                      })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {templatePaging.total > PAGE_SIZE && (
                            <div className='mailchimp2-paging'>
                                <Paging
                                    totalItems={templatePaging.total}
                                    currentPage={templatePaging.page}
                                    pageSize={PAGE_SIZE}
                                    loading={false}
                                    changePage={setTemplatePage}
                                />
                            </div>
                        )}
                    </React.Fragment>
                )}
            </Paper>


            <Paper className='mailchimp2-card'>
                <div className='mailchimp2-card__head'>
                    <h3>{i18n.t('mailchimp2:preview.campaigns')}</h3>
                    {campaignEligibleIds.length > 0 && (
                        <SelectAllToggle
                            allSelected={campaignsAllSelected}
                            onToggle={toggleAllCampaigns}
                            disabled={campaignEligibleIds.length === 0}
                        />
                    )}
                </div>
                <p>
                    {i18n.t('mailchimp2:preview.campaignsLead', {
                        count: selectedCampaignIds.length
                    })}
                </p>
                {campaignPaging.pageItems.map((campaign) => {
                    const audienceReady = campaignAudienceSelected(campaign)
                    const checked = audienceReady && campaignOn(campaign.id)
                    const tooltipId = 'mailchimp2-campaign-' + campaign.id
                    const disabledReason = campaign.list_name
                        ? i18n.t('mailchimp2:preview.campaignNeedsAudience', {
                              audience: campaign.list_name
                          })
                        : i18n.t('mailchimp2:preview.campaignNeedsAudienceGeneric')
                    const rowClass =
                        'mailchimp2-row mailchimp2-row--pick' +
                        (audienceReady ? '' : ' is-disabled') +
                        (checked ? '' : ' is-off')
                    return (
                        <div
                            className={rowClass}
                            key={campaign.id}
                            data-tooltip-id={audienceReady ? undefined : tooltipId}
                            data-tooltip-content={audienceReady ? undefined : disabledReason}
                            onClick={(event) => {
                                if (!audienceReady) return
                                if (event.target.closest && event.target.closest('.checkboxWrapper')) return
                                toggleCampaign(campaign.id, !campaignOn(campaign.id))
                            }}
                        >
                            <CheckBox
                                checked={checked}
                                disabled={!audienceReady}
                                onClick={(event) => {
                                    if (!audienceReady) return
                                    toggleCampaign(campaign.id, event.target.checked)
                                }}
                            />
                            <div>
                                <div className='mailchimp2-row__name'>
                                    {(campaign.title && String(campaign.title).trim()) ||
                                        i18n.t('mailchimp2:preview.untitledCampaign')}
                                </div>
                                <div>
                                    {[
                                        campaign.list_name,
                                        campaign.template_name,
                                        DateTimeFunctions.formatDateTime(campaign.create_time, '2')
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </div>
                            </div>
                        </div>
                    )
                })}
                {campaignPaging.pageItems
                    .filter((campaign) => !campaignAudienceSelected(campaign))
                    .map((campaign) => (
                        <Tooltip key={'tip-' + campaign.id} id={'mailchimp2-campaign-' + campaign.id} />
                    ))}
                {campaignPaging.total > PAGE_SIZE && (
                    <div className='mailchimp2-paging'>
                        <Paging
                            totalItems={campaignPaging.total}
                            currentPage={campaignPaging.page}
                            pageSize={PAGE_SIZE}
                            loading={false}
                            changePage={setCampaignPage}
                        />
                    </div>
                )}
            </Paper>

            {dndBlocked && (
                <Alert type='warning' className='mar-t16' content={i18n.t('mailchimp2:preview.dndLimit')} />
            )}
            <div className='mailchimp2-actions'>
                <Button
                    text={i18n.t('mailchimp2:preview.start')}
                    className='button-primary'
                    onClick={() =>
                        onStart({
                            listIds: selectedIds,
                            templateIds: selectedTemplateIds,
                            campaignIds: selectedCampaignIds,
                            fieldTags: selectedFieldTags
                        })
                    }
                    disabled={busy || selectedIds.length === 0 || dndBlocked}
                />
                <Button
                    text={i18n.t('mailchimp2:preview.notNow')}
                    className='button-sml grey'
                    onClick={onNotNow}
                    disabled={busy}
                />
            </div>
            {previewTemplate &&
                (previewTemplate.thumbnail || previewTemplate.sourceCampaignId) &&
                ReactDOM.createPortal(
                    <div
                        className='mailchimp2-lightbox'
                        role='dialog'
                        aria-modal='true'
                        aria-label={previewTemplate.name}
                        onClick={() => setPreviewTemplate(null)}
                    >
                        <button
                            type='button'
                            className='mailchimp2-lightbox__close'
                            onClick={() => setPreviewTemplate(null)}
                            aria-label={i18n.t('mailchimp2:preview.closePreview')}
                        >
                            <Icons icon='cross' color='#fff' style={{ width: 18, height: 18 }} />
                        </button>
                        {previewTemplate.sourceCampaignId ? (
                            <div className='mailchimp2-lightbox__panel' onClick={(event) => event.stopPropagation()}>
                                {previewLoading && (
                                    <div className='mailchimp2-lightbox__status'>
                                        {i18n.t('mailchimp2:preview.previewLoading')}
                                    </div>
                                )}
                                {previewError && <div className='mailchimp2-lightbox__status'>{previewError}</div>}
                                {previewHtml && (
                                    <iframe
                                        className='mailchimp2-lightbox__frame'
                                        title={previewTemplate.name}
                                        sandbox=''
                                        srcDoc={previewHtml}
                                    />
                                )}
                            </div>
                        ) : (
                            <img
                                className='mailchimp2-lightbox__img'
                                src={previewTemplate.thumbnail}
                                alt={previewTemplate.name}
                                onClick={(event) => event.stopPropagation()}
                            />
                        )}
                    </div>,
                    document.body
                )}
        </div>
    )
}

function DoneStatTile({ path, value, label, wide, onNavigate, onClick }) {
    if (!value) return null
    const clickable = !!((path && onNavigate) || onClick)
    return (
        <Paper
            className={
                (clickable ? 'paper-clickable ' : '') +
                'paper-padding-16 mar-b0' +
                (wide ? ' mailchimp2-stat-tile--wide' : '')
            }
            onClick={clickable ? onClick || (() => onNavigate(path)) : undefined}
        >
            <div className='statistic mar-b5' style={{ fontSize: 32, lineHeight: 1.1 }}>
                {FormatNumberFuncs.formatNumber(value)}
            </div>
            <div className='text-grey'>{label}</div>
        </Paper>
    )
}

function Done({ job, disconnect, importAgain }) {
    const history = useHistory()
    const [imagesOpen, setImagesOpen] = useState(false)
    const counts = (job && job.counts) || {}
    const listIds = (job && job.listIds) || []
    const listMapping = (job && job.mapping && job.mapping.lists) || {}
    const resolveGroupId = (listId) => listMapping[listId] || listMapping[String(listId)]
    const audienceCount = listIds.length || Object.keys(listMapping).length
    const singleGroupId =
        audienceCount === 1 ? (listIds.length === 1 ? resolveGroupId(listIds[0]) : Object.values(listMapping)[0]) : null
    const contactsPath = singleGroupId ? `/cp/groups/${singleGroupId}` : '/cp/subscribers/groups'
    const groupsPath = singleGroupId ? `/cp/groups/${singleGroupId}` : '/cp/subscribers/groups'
    const contactsLabel = singleGroupId ? i18n.t('mailchimp2:done.viewContacts') : i18n.t('mailchimp2:done.viewGroups')
    const go = (path) => history.push(path)
    const created = counts.subscribers || 0
    const updated = counts.subscribersUpdated || 0

    return (
        <div className='mailchimp2-page'>
            <Mailchimp2Icons />
            <h1 className='mailchimp2-title'>{i18n.t('mailchimp2:done.title')}</h1>
            <p className='mailchimp2-lead'>{i18n.t('mailchimp2:done.lead')}</p>
            <div className='mailchimp2-stats'>
                <DoneStatTile
                    wide
                    path={contactsPath}
                    value={created}
                    label={i18n.t('mailchimp2:done.tile.contacts')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path={contactsPath}
                    value={updated}
                    label={i18n.t('mailchimp2:done.tile.contactsUpdated')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path={groupsPath}
                    value={counts.groups}
                    label={i18n.t('mailchimp2:done.tile.groups')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path={groupsPath}
                    value={counts.groupsUpdated}
                    label={i18n.t('mailchimp2:done.tile.groupsUpdated')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path='/cp/subscribers/customfields'
                    value={counts.fields}
                    label={i18n.t('mailchimp2:done.tile.fields')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path='/cp/subscribers/customfields'
                    value={counts.fieldsUpdated}
                    label={i18n.t('mailchimp2:done.tile.fieldsUpdated')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path='/cp/templates'
                    value={counts.templates}
                    label={i18n.t('mailchimp2:done.tile.templates')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path='/cp/templates'
                    value={counts.templatesUpdated}
                    label={i18n.t('mailchimp2:done.tile.templatesUpdated')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path='/cp/campaigns'
                    value={counts.campaigns}
                    label={i18n.t('mailchimp2:done.tile.campaigns')}
                    onNavigate={go}
                />
                <DoneStatTile
                    path='/cp/campaigns'
                    value={counts.campaignsUpdated}
                    label={i18n.t('mailchimp2:done.tile.campaignsUpdated')}
                    onNavigate={go}
                />
                <DoneStatTile
                    value={counts.images || 0}
                    label={i18n.t('mailchimp2:done.tile.images')}
                    onClick={() => setImagesOpen(true)}
                />
                <DoneStatTile
                    value={counts.imagesUpdated || 0}
                    label={i18n.t('mailchimp2:done.tile.imagesUpdated')}
                    onClick={() => setImagesOpen(true)}
                />
            </div>
            <div className='mailchimp2-actions'>
                <Button text={contactsLabel} className='button-primary' onClick={() => go(contactsPath)} />
                <Button
                    text={i18n.t('mailchimp2:done.importAgain')}
                    className='button-sml grey'
                    onClick={importAgain}
                />
                <Button
                    text={i18n.t('mailchimp2:done.disconnect')}
                    className='button-sml grey'
                    onClick={disconnect}
                />
            </div>
            {imagesOpen &&
                ReactDOM.createPortal(
                    <Drawer
                        standalone
                        showClose
                        width='100vw'
                        className='fix-height'
                        close={() => setImagesOpen(false)}
                    >
                        <FileManager fileTypes='images' initialFolderName='mailchimp' />
                    </Drawer>,
                    document.body
                )}
        </div>
    )
}
